import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../../supabaseClient';

export default function DraftOrderChallenge({ poolId, userId, isCreator, lang = 'en' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [pool, setPool] = useState(null);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [clockOffset, setClockOffset] = useState(0); // ms to add to client Date.now() to approximate server time
  const tickRef = useRef(null);

  // Calibrate client-server clock offset on mount. Takes the median of 3 samples.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const samples = [];
      for (let i = 0; i < 3; i++) {
        const start = Date.now();
        const { data, error: err } = await supabase.rpc('server_now');
        const end = Date.now();
        if (cancelled || err || !data) continue;
        const serverMs = new Date(data).getTime();
        const clientMid = (start + end) / 2;
        samples.push(serverMs - clientMid);
      }
      if (cancelled || samples.length === 0) return;
      samples.sort((a, b) => a - b);
      setClockOffset(samples[Math.floor(samples.length / 2)]);
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchState = useCallback(async () => {
    const [{ data: p }, { data: m }] = await Promise.all([
      supabase.from('pools').select('*').eq('id', poolId).single(),
      supabase.from('pool_members')
        .select('user_id, draft_slot, challenge_ready, challenge_reaction_ms, profiles(username)')
        .eq('pool_id', poolId)
        .order('draft_slot', { ascending: true }),
    ]);
    if (p) setPool(p);
    if (m) setMembers(m);
  }, [poolId]);

  useEffect(() => { fetchState(); }, [fetchState]);

  useEffect(() => {
    const ch = supabase
      .channel(`challenge-${poolId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pools', filter: `id=eq.${poolId}` }, fetchState)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pool_members', filter: `pool_id=eq.${poolId}` }, fetchState)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [poolId, fetchState]);

  // Tick at 50ms while armed so the green flash is snappy.
  useEffect(() => {
    const interval = pool?.challenge_state === 'armed' ? 50 : 500;
    tickRef.current = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(tickRef.current);
  }, [pool?.challenge_state]);

  const call = async (name) => {
    setError(''); setBusy(true);
    const { data, error: err } = await supabase.rpc(name, { p_user_id: userId, p_pool_id: poolId });
    setBusy(false);
    if (err) { setError(err.message); return null; }
    if (!data?.success) { setError(data?.error || 'Error'); return null; }
    return data;
  };

  const myMember = members.find((m) => m.user_id === userId);
  const state = pool?.challenge_state || 'none';
  const armedAt = pool?.challenge_armed_at ? new Date(pool.challenge_armed_at).getTime() : null;
  // Use offset-corrected time so clients with skewed clocks see green in sync with the server.
  const serverNow = now + clockOffset;
  const isGreen = state === 'armed' && armedAt && serverNow >= armedAt;
  const msUntilGreen = armedAt ? Math.max(0, armedAt - serverNow) : null;

  if (state === 'none') {
    if (!isCreator) return null;
    if (!members || members.length < 2) return null;
    return (
      <div className="challenge-box idle">
        <div>
          <div className="challenge-title">{t.title}</div>
          <div className="challenge-desc">{t.desc}</div>
        </div>
        <button className="pool-start-btn" onClick={() => call('start_order_challenge')} disabled={busy}>
          {t.startChallenge}
        </button>
      </div>
    );
  }

  if (state === 'waiting') {
    const readyCount = members.filter((m) => m.challenge_ready).length;
    return (
      <div className="challenge-box waiting">
        <div className="challenge-title">{t.readyTitle}</div>
        <div className="challenge-desc">
          {t.readyDesc} — {readyCount}/{members.length} {t.ready}
        </div>
        <div className="challenge-members">
          {members.map((m) => (
            <span key={m.user_id} className={`challenge-member ${m.challenge_ready ? 'ready' : ''}`}>
              {m.challenge_ready ? '✓ ' : '○ '}{m.profiles?.username}
            </span>
          ))}
        </div>
        <div className="challenge-actions">
          {!myMember?.challenge_ready && (
            <button className="pool-start-btn" onClick={() => call('mark_ready')} disabled={busy}>
              {t.imReady}
            </button>
          )}
          {myMember?.challenge_ready && (
            <div style={{ color: '#22c55e', fontWeight: 700 }}>✓ {t.youAreReady}</div>
          )}
          {isCreator && (
            <button className="pool-leave-btn" onClick={() => call('cancel_order_challenge')} disabled={busy}>
              {t.cancel}
            </button>
          )}
        </div>
        {error && <div className="pool-form-error">{error}</div>}
      </div>
    );
  }

  if (state === 'armed') {
    const alreadyClicked = myMember?.challenge_reaction_ms != null;
    return (
      <div className={`challenge-box armed ${isGreen ? 'green' : 'red'}`}>
        <div className="challenge-title-big">
          {alreadyClicked ? t.waitOthers
            : isGreen ? t.clickNow
            : t.dontClickYet}
        </div>
        <div className="challenge-subtitle">
          {alreadyClicked
            ? `${t.yourTime} ${formatReaction(myMember.challenge_reaction_ms, t)}`
            : isGreen ? 'GO!'
            : `${t.getReady}`}
        </div>
        {!alreadyClicked && (
          <button
            className={`challenge-click-btn ${isGreen ? 'active' : 'disabled'}`}
            onClick={() => call('record_reaction')}
            disabled={busy}
          >
            {isGreen ? t.click : t.wait}
          </button>
        )}
        <div className="challenge-members small">
          {members.map((m) => (
            <span key={m.user_id} className={`challenge-member ${m.challenge_reaction_ms != null ? 'ready' : ''}`}>
              {m.challenge_reaction_ms != null ? `✓ ${m.profiles?.username}` : `○ ${m.profiles?.username}`}
            </span>
          ))}
        </div>
        {isCreator && (
          <button className="pool-leave-btn" style={{ marginTop: 12 }} onClick={() => call('cancel_order_challenge')} disabled={busy}>
            {t.cancel}
          </button>
        )}
        {error && <div className="pool-form-error">{error}</div>}
      </div>
    );
  }

  if (state === 'finished') {
    const sorted = [...members].sort((a, b) =>
      (a.challenge_reaction_ms ?? 99999) - (b.challenge_reaction_ms ?? 99999));
    return (
      <div className="challenge-box finished">
        <div className="challenge-title">{t.resultsTitle}</div>
        <div className="challenge-results">
          {sorted.map((m, idx) => (
            <div key={m.user_id} className={`challenge-result-row slot-${idx + 1}`}>
              <span className="challenge-result-rank">#{idx + 1}</span>
              <span className="challenge-result-name">{m.profiles?.username}</span>
              <span className="challenge-result-time">{formatReaction(m.challenge_reaction_ms, t)}</span>
            </div>
          ))}
        </div>
        {isCreator && (
          <div className="challenge-actions">
            <button className="pool-start-btn" onClick={() => call('apply_order_challenge')} disabled={busy}>
              {t.applyOrder}
            </button>
            <button className="pool-leave-btn" onClick={() => call('start_order_challenge')} disabled={busy}>
              {t.tryAgain}
            </button>
          </div>
        )}
        {!isCreator && <div style={{ color: '#666', fontSize: 13 }}>{t.waitingCreator}</div>}
        {error && <div className="pool-form-error">{error}</div>}
      </div>
    );
  }

  return null;
}

function formatReaction(ms, t) {
  if (ms == null) return '—';
  if (ms >= 99999) return t.falseStart;
  return `${ms} ms`;
}

const TR = {
  en: {
    title: 'Draft Order Challenge',
    desc: 'Fastest reaction gets the #1 pick. Click Ready, wait for the green flash, then smash the button.',
    startChallenge: 'Start Reaction Challenge',
    readyTitle: 'Draft Order Challenge — Ready?',
    readyDesc: 'When everyone clicks Ready, the clock starts. A green flash will appear after a random 3-7 second wait.',
    ready: 'ready',
    imReady: "I'm Ready!",
    youAreReady: "You're ready. Waiting for others…",
    dontClickYet: 'WAIT…',
    getReady: 'Green will flash in a moment — do NOT click early!',
    clickNow: 'CLICK NOW!',
    click: 'CLICK!',
    wait: 'WAIT',
    waitOthers: 'Waiting for other players to click…',
    yourTime: 'Your time:',
    resultsTitle: 'Results',
    applyOrder: 'Apply This Order',
    tryAgain: 'Re-run Challenge',
    waitingCreator: 'Waiting for the creator to apply the order…',
    cancel: 'Cancel Challenge',
    falseStart: 'False start',
  },
  fr: {
    title: 'Défi ordre de repêchage',
    desc: 'Le plus rapide reçoit le premier choix. Cliquez Prêt, attendez le flash vert, puis frappez.',
    startChallenge: 'Lancer le défi',
    readyTitle: 'Défi ordre — Prêt?',
    readyDesc: 'Quand tout le monde est prêt, un flash vert apparaît après 3 à 7 secondes.',
    ready: 'prêts',
    imReady: 'Je suis prêt!',
    youAreReady: 'Vous êtes prêt. En attente des autres…',
    dontClickYet: 'ATTENDEZ…',
    getReady: 'Le flash vert arrive — ne cliquez PAS trop tôt!',
    clickNow: 'CLIQUEZ!',
    click: 'CLIQUEZ!',
    wait: 'ATTENDEZ',
    waitOthers: 'En attente des autres joueurs…',
    yourTime: 'Votre temps :',
    resultsTitle: 'Résultats',
    applyOrder: 'Appliquer cet ordre',
    tryAgain: 'Recommencer',
    waitingCreator: "En attente que le créateur applique l'ordre…",
    cancel: 'Annuler le défi',
    falseStart: 'Faux départ',
  },
};
